import { Currency, SelectKeyboardListenersNames } from './types';
import cloneDeep from 'lodash/cloneDeep';
import { CLOUD_TYPE_IDS, DeprecatedUsersAccountTypeId } from 'users/constants/usersConstants';
import { ViewTypes } from './processingCurrencyViewType.enum';
import { BaseRoutes, Routes } from 'shared/constants/routes';

export const isViewSupportsCurrencyFeatureCurrentPath = (location: string): boolean => {
  if (
    location.includes(`${BaseRoutes.CostAndUsage}/dashboards`) ||
    location.includes(`${BaseRoutes.CostAndUsage}/dashboards-templates`)
  ) {
    return true;
  }

  switch (location) {
    case Routes.DASHBOARD:
    case Routes.AWS_CUSTOMERS:
    case Routes.CUSTOMERS_HISTORY:
    case Routes.AZURE_CUSTOMERS_HISTORY:
    case Routes.AZURE_CUSTOMERS:
    case Routes.COST_USAGE_EXPLORER:
    case Routes.RESOURCE_EXPLORER:
    case Routes.CUSTOM_DASHBOARD:
    case Routes.CUSTOM_DASHBOARD_TEMPLATE:
    case Routes.CUSTOM_DASHBOARDS_PANELS:
    case Routes.ASSETS:
    case Routes.K8S_PREVIEW:
    case Routes.K8S_USAGE:
    case Routes.K8S_PREFERENCES:
    case Routes.K8S_ASSETS: {
      return true;
    }
    default: {
      return false;
    }
  }
};

export const isShowingReportsView = (location: string): boolean => {
  switch (location) {
    case Routes.CUSTOMERS_REPORTS:
    case Routes.REPORTS: {
      return true;
    }
    default: {
      return false;
    }
  }
};

export const getViewType = (cloudTypeId, accountType, isM3c) => {
  switch (true) {
    case cloudTypeId === CLOUD_TYPE_IDS.MULTI && !isM3c:
    case cloudTypeId === CLOUD_TYPE_IDS.AZURE:
    case [
      DeprecatedUsersAccountTypeId.MULTI_CLOUDS_ACCOUNT,
      DeprecatedUsersAccountTypeId.RESELLER_MULTI_CLOUDS_ACCOUNT,
    ].includes(accountType): {
      return ViewTypes.AzureExchangeRate;
    }
    default: {
      return ViewTypes.DefaultExchangeRate;
    }
  }
};

export const getCurrenciesArray = (
  sessionCurrencyCode,
  processingCurrency,
  providerCurrency,
  currenciesArray: Currency[],
): Currency[] => {
  return cloneDeep(currenciesArray)
    .map((currency) => {
      let order;
      switch (currency.code) {
        case sessionCurrencyCode: {
          order = 5;
          break;
        }
        case processingCurrency: {
          order = 4;
          break;
        }
        case providerCurrency: {
          order = 3;
          break;
        }
        case 'USD': {
          order = 2;
          break;
        }
        case 'EUR': {
          order = 1;
          break;
        }
        default: {
          order = 0;
          break;
        }
      }
      return {
        symbol: currency.symbol,
        name: currency.name,
        code: currency.code,
        namePlural: currency.namePlural,
        symbolNative: currency.symbolNative,
        subTitle: getCurrencySubTitle(currency, sessionCurrencyCode, processingCurrency, providerCurrency),
        isProviderCurrency: currency.code === providerCurrency,
        isProcessingCurrency: currency.code === processingCurrency,
        isSessionCurrency: currency.code === sessionCurrencyCode,
        order,
      };
    })
    .sort((a, b) => {
      return b.order - a.order;
    });
};

export const getCurrencySubTitle = (
  currency: Currency,
  sessionCurrency: string,
  processingCurrency: string,
  providerCurrency: string,
) => {
  if (currency.code === sessionCurrency) {
    return 'Session currency';
  }
  if (currency.code === processingCurrency) {
    return 'Processing currency';
  }
  if (currency.code === providerCurrency) {
    return 'Provider currency';
  }
  return '';
};

export const getKeyPressCallback = ({
  filteredOptions,
  currenciesArray,
  keyboardSelectedCurrency,
  setSearchInputValue,
  setSelectedCurrency,
  setMenuClosed,
  setKeyboardSelectedCurrency,
  setKeyPressDirection,
}: {
  filteredOptions: Currency[];
  currenciesArray: Currency[];
  keyboardSelectedCurrency: Currency | null;
  setSearchInputValue: React.Dispatch<React.SetStateAction<string>>;
  setSelectedCurrency: React.Dispatch<React.SetStateAction<string>>;
  setMenuClosed: React.Dispatch<React.SetStateAction<boolean | null>>;
  setKeyboardSelectedCurrency: React.Dispatch<React.SetStateAction<Currency | null>>;
  setKeyPressDirection: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  return (key: { code: string } | null, searchValue?: string) => {
    if (key === null && searchValue?.length) {
      handleSearchInput(setSearchInputValue, searchValue);
      return;
    }

    if (!key) return;

    if (
      !searchValue &&
      [SelectKeyboardListenersNames.BACKSPACE, SelectKeyboardListenersNames.DELETE].includes(
        key.code as SelectKeyboardListenersNames,
      )
    ) {
      handleSearchInput(setSearchInputValue, '');
      return;
    }

    const optionsToUse = filteredOptions.length > 0 ? filteredOptions : currenciesArray;
    setKeyPressDirection(key.code);
    switch (key.code) {
      case SelectKeyboardListenersNames.ENTER: {
        handleEnterKey(keyboardSelectedCurrency, setSelectedCurrency);
        break;
      }
      case SelectKeyboardListenersNames.ESCAPE: {
        handleEscapeKey(setMenuClosed);
        break;
      }
      case SelectKeyboardListenersNames.ARROW_DOWN: {
        handleArrowDownKey(optionsToUse, keyboardSelectedCurrency, setKeyboardSelectedCurrency);
        break;
      }
      case SelectKeyboardListenersNames.ARROW_UP: {
        handleArrowUpKey(optionsToUse, keyboardSelectedCurrency, setKeyboardSelectedCurrency);
        break;
      }
      default: {
        break;
      }
    }
  };
};

export const getCurrenciesOptions = ({
  keyboardSelectedCurrency,
  filteredOptions,
  currenciesArray,
  selectedRef,
  sessionCurrency,
  processingCurrency,
  providerCurrency,
}: {
  keyboardSelectedCurrency: Currency | null;
  filteredOptions: Currency[];
  currenciesArray: Currency[];
  selectedRef: React.MutableRefObject<HTMLDivElement | null>;
  sessionCurrency;
  processingCurrency;
  providerCurrency;
}) => {
  const keyboardMarkedCurrency = keyboardSelectedCurrency || (filteredOptions.length > 0 ? filteredOptions[0] : null);

  const optionsToUse = filteredOptions.length > 0 ? filteredOptions : currenciesArray;

  const localOptions = optionsToUse.map((currency) => {
    const isFocusedByKeyboard = keyboardMarkedCurrency?.code === currency.code;

    return {
      ref: isFocusedByKeyboard ? selectedRef : null,
      label: `${currency.code} (${currency?.symbolNative}) - ${currency.namePlural}`,
      value: currency.code,
      isFocusedByKeyboard: !!isFocusedByKeyboard,
      innerDescription: getCurrencySubTitle(currency, sessionCurrency, processingCurrency, providerCurrency),
    };
  });
  return localOptions;
};

export const filterCurrencies = (currency: Currency[], searchText: string): Currency[] => {
  const data = currency?.filter((currency) => {
    if (!searchText) {
      return true;
    }
    searchText = searchText?.toLowerCase?.();
    return (
      currency?.symbol?.toLowerCase?.()?.includes(searchText) ||
      currency?.code?.toLowerCase?.()?.includes(searchText) ||
      currency?.name?.toLowerCase?.()?.includes(searchText) ||
      currency?.symbolNative?.toLowerCase?.()?.includes(searchText) ||
      currency?.namePlural?.toLowerCase?.()?.includes(searchText)
    );
  });
  return data;
};

export const scrollToOption = (
  selectedRef: React.MutableRefObject<HTMLDivElement | null>,
  keyDirection: SelectKeyboardListenersNames | null,
) => {
  const menuElement = selectedRef.current?.closest('.app-select__menu-list');

  if (!menuElement || !selectedRef.current) {
    return;
  }

  const allOptions = menuElement.querySelectorAll('.app-select__option');
  const targetIndex = getTargetIndex(allOptions, selectedRef);

  if (targetIndex === -1) {
    return;
  }

  const optionHeight = allOptions[0].getBoundingClientRect().height;
  const menuRect = menuElement.getBoundingClientRect();
  const visibleHeight = menuRect.height;
  const totalContentHeight = optionHeight * allOptions.length;
  const maxScrollTop = totalContentHeight - visibleHeight;
  const visibleOptions = Math.floor(visibleHeight / optionHeight);
  const scrollPosition = getScrollPosition(
    keyDirection,
    targetIndex,
    allOptions,
    visibleOptions,
    maxScrollTop,
    optionHeight,
  );

  menuElement.scrollTop = scrollPosition;

  setTimeout(() => {
    executeScroll(selectedRef, menuElement);
  }, 0);
};

const handleSearchInput = (setSearchInputValue: React.Dispatch<React.SetStateAction<string>>, searchValue?: string) => {
  if (searchValue !== undefined) {
    setSearchInputValue(searchValue);
  }
};

const handleEnterKey = (
  keyboardSelectedCurrency: Currency | null,
  setSelectedCurrency: React.Dispatch<React.SetStateAction<string>>,
) => {
  if (keyboardSelectedCurrency) {
    setSelectedCurrency(keyboardSelectedCurrency?.code);
  }
};

const handleEscapeKey = (setMenuClosed: React.Dispatch<React.SetStateAction<boolean | null>>) => {
  setMenuClosed(true);
};

const handleArrowDownKey = (
  optionsToUse: Currency[],
  keyboardSelectedCurrency: Currency | null,
  setKeyboardSelectedCurrency: React.Dispatch<React.SetStateAction<Currency | null>>,
) => {
  const currentIndex = optionsToUse.findIndex((currency) => currency.code === keyboardSelectedCurrency?.code);

  if (currentIndex === -1 || currentIndex === optionsToUse.length - 1) {
    setKeyboardSelectedCurrency(optionsToUse[0]);
  } else {
    setKeyboardSelectedCurrency(optionsToUse[currentIndex + 1]);
  }
};

const handleArrowUpKey = (
  optionsToUse: Currency[],
  keyboardSelectedCurrency: Currency | null,
  setKeyboardSelectedCurrency: React.Dispatch<React.SetStateAction<Currency | null>>,
) => {
  const currentIndex = optionsToUse.findIndex((currency) => currency.code === keyboardSelectedCurrency?.code);

  if (currentIndex <= 0) {
    setKeyboardSelectedCurrency(optionsToUse[optionsToUse.length - 1]);
  } else {
    setKeyboardSelectedCurrency(optionsToUse[currentIndex - 1]);
  }
};

const executeScroll = (selectedRef: React.MutableRefObject<HTMLDivElement | null>, menuElement: Element): void => {
  const optionRect = selectedRef.current?.getBoundingClientRect();
  const updatedMenuRect = menuElement.getBoundingClientRect();

  const isVisible = optionRect && optionRect.top >= updatedMenuRect.top && optionRect.bottom <= updatedMenuRect.bottom;

  if (!isVisible) {
    if (optionRect && optionRect.top < updatedMenuRect.top) {
      menuElement.scrollTop -= updatedMenuRect.top - optionRect.top + 5;
    } else if (optionRect && optionRect.bottom > updatedMenuRect.bottom) {
      menuElement.scrollTop += optionRect.bottom - updatedMenuRect.bottom + 5;
    }
  }
};

const getScrollPosition = (
  keyDirection: SelectKeyboardListenersNames | null,
  targetIndex: number,
  allOptions: NodeListOf<Element>,
  visibleOptions: number,
  maxScrollTop: number,
  optionHeight: number,
): number => {
  let scrollPosition;

  if (keyDirection === SelectKeyboardListenersNames.ARROW_DOWN) {
    if (targetIndex >= allOptions.length - visibleOptions) {
      scrollPosition = maxScrollTop;
    } else {
      const buffer = Math.min(1, targetIndex);
      scrollPosition = (targetIndex - buffer) * optionHeight;
    }
  } else if (keyDirection === SelectKeyboardListenersNames.ARROW_UP) {
    if (targetIndex <= visibleOptions / 2) {
      scrollPosition = 0;
    } else {
      const itemsBelow = 1;
      scrollPosition = (targetIndex - (visibleOptions - itemsBelow - 1)) * optionHeight;
      scrollPosition = Math.max(0, scrollPosition);
    }
  } else {
    scrollPosition = (targetIndex - Math.floor(visibleOptions / 2)) * optionHeight;
    scrollPosition = Math.max(0, scrollPosition);
  }

  scrollPosition = Math.min(scrollPosition, maxScrollTop);
  scrollPosition = Math.max(0, scrollPosition);
  return scrollPosition;
};

const getTargetIndex = (
  allOptions: NodeListOf<Element>,
  selectedRef: React.MutableRefObject<HTMLDivElement | null>,
): number => {
  let targetIndex = -1;
  for (let i = 0; i < allOptions.length; i++) {
    if (allOptions[i].contains(selectedRef.current)) {
      targetIndex = i;
      break;
    }
  }
  return targetIndex;
};

export const addCurrenciesHeadersToHttpRequest = (
  payload: { [key: string]: unknown },
  useCurrency: boolean,
  sessionCurrencyCode: string,
) => {
  const headers = cloneDeep(payload);
  if (useCurrency) {
    headers['Frontend-Request'] = true;
    if (sessionCurrencyCode) {
      headers['Session-Currency'] = sessionCurrencyCode;
    }
  }

  return headers;
};
